import React from 'react';
import { formatCurrency } from '../utils/dateUtils.ts';
import CapitalDepletionAlert from './CapitalDepletionAlert.tsx';
import './InvestmentProjection.css';

interface ProjectionPoint {
  month_index: number;
  age: number;
  nominal_net_worth: number;
  real_net_worth: number;
  contribution: number;
  withdrawal: number;
  phase: string;
}

interface ProjectionOutput {
  points: ProjectionPoint[];
  cash_flows: Array<{
    month_index: number;
    age: number;
    contribution: number;
    withdrawal: number;
  }>;
  annual_summaries?: Array<{
    year_index: number;
    age: number;
    nominal_net_worth: number;
    real_net_worth: number;
    total_contribution: number;
    total_withdrawal: number;
  }>;
  depletion_alert?: {
    depletes: boolean;
    depletion_month: number;
    depletion_age: number;
  };
}

interface InvestmentProjectionProps {
  projection: ProjectionOutput | null;
  alert?: ProjectionOutput['depletion_alert'] | null;
}

const InvestmentProjection: React.FC<InvestmentProjectionProps> = ({ projection, alert }) => {
  if (!projection || projection.points.length === 0) {
    return (
      <div className="projection-empty">
        Nenhuma projeção disponível. Gere uma projeção para visualizar os resultados.
      </div>
    );
  }

  const lastPoint = projection.points[projection.points.length - 1];

  const annualSummaries = projection.annual_summaries || [];

  return (
    <div className="projection-container">
      <div className="projection-summary">
        <div>
          <span className="label">Patrimônio final (nominal)</span>
          <strong>{formatCurrency(lastPoint.nominal_net_worth)}</strong>
        </div>
        <div>
          <span className="label">Patrimônio final (real)</span>
          <strong>{formatCurrency(lastPoint.real_net_worth)}</strong>
        </div>
        <div>
          <span className="label">Idade final</span>
          <strong>{lastPoint.age.toFixed(1)} anos</strong>
        </div>
      </div>
      <CapitalDepletionAlert alert={alert} />

      {annualSummaries.length > 0 && (
        <div className="projection-annual">
          <h4>Resumo anual</h4>
          <div className="projection-row projection-header">
            <span>Ano</span>
            <span>Idade</span>
            <span>Nominal</span>
            <span>Real</span>
            <span>Aporte</span>
            <span>Retirada</span>
          </div>
          {annualSummaries.map((year) => (
            <div className="projection-row" key={year.year_index}>
              <span>{year.year_index}</span>
              <span>{year.age.toFixed(1)}</span>
              <span>{formatCurrency(year.nominal_net_worth)}</span>
              <span>{formatCurrency(year.real_net_worth)}</span>
              <span>{formatCurrency(year.total_contribution)}</span>
              <span>{formatCurrency(year.total_withdrawal)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default InvestmentProjection;
